import React, { useEffect, useMemo, useState } from "react";
import { Button, Input, Modal, Select, Table } from "antd";
import dayjs from "dayjs";
import {
  exportEventsCsv,
  getEventLocation,
  getEventPlatform,
  getEventVersion,
} from "./chartHelpers";
import { analyticsModalOverlayProps } from "./analyticsModalUtils";

const formatEventDetails = (row) => {
  const p = row.properties || {};
  const bits = [
    p.step_name && `step: ${p.step_name}`,
    p.form_name && `form: ${p.form_name}`,
    p.page_name && `page: ${p.page_name}`,
    p.feature_name && `feature: ${p.feature_name}`,
    p.product_name && `product: ${p.product_name}`,
    p.error_message && `error: ${p.error_message}`,
    p.abandon_reason && `reason: ${p.abandon_reason}`,
    p.status_code && `status: ${p.status_code}`,
  ].filter(Boolean);
  return bits.length ? bits.join(" · ") : "—";
};

const filterEvents = (events, filters) => {
  const q = (filters.search || "").trim().toLowerCase();
  return (events || []).filter((ev) => {
    const p = ev.properties || {};
    if (filters.event && filters.event !== "all" && ev.event !== filters.event) return false;
    if (filters.platform && filters.platform !== "all") {
      const platform = getEventPlatform(p).toLowerCase();
      if (platform !== filters.platform.toLowerCase()) return false;
    }
    if (filters.version && filters.version !== "all") {
      const version = getEventVersion(p);
      if (version !== filters.version) return false;
    }
    if (!q) return true;
    const haystack = [
      ev.event,
      p.page_name,
      p.step_name,
      p.feature_name,
      p.product_name,
      p.error_message,
      getEventPlatform(p),
      getEventVersion(p),
      getEventLocation(p),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
};

const buildFilterOptions = (events) => {
  const eventNames = new Set();
  const platforms = new Set();
  const versions = new Set();
  (events || []).forEach((ev) => {
    if (ev.event) eventNames.add(ev.event);
    const p = ev.properties || {};
    const platform = getEventPlatform(p);
    const version = getEventVersion(p);
    if (platform) platforms.add(platform);
    if (version) versions.add(version);
  });
  return {
    events: Array.from(eventNames).sort(),
    platforms: Array.from(platforms).sort(),
    versions: Array.from(versions).sort(),
  };
};

const eventTableColumns = [
  {
    title: "Time",
    dataIndex: "timestamp",
    key: "time",
    width: 150,
    render: (v) => (v ? dayjs(v).format("D MMM HH:mm:ss") : "—"),
  },
  {
    title: "Event",
    dataIndex: "event",
    key: "event",
    width: 170,
    render: (v) => <code className="analytics-code-sm">{v || "—"}</code>,
  },
  {
    title: "Screen",
    key: "screen",
    width: 130,
    render: (_, row) => row.properties?.page_name || row.properties?.screen_name || "—",
  },
  {
    title: "Version",
    key: "version",
    width: 90,
    render: (_, row) => getEventVersion(row.properties || {}) || "—",
  },
  {
    title: "Location",
    key: "location",
    width: 140,
    ellipsis: true,
    render: (_, row) => getEventLocation(row.properties || {}) || "—",
  },
  {
    title: "Details",
    key: "details",
    ellipsis: true,
    render: (_, row) => formatEventDetails(row),
  },
  {
    title: "Device / Platform",
    key: "device",
    width: 150,
    render: (_, row) => {
      const p = row.properties || {};
      const platform = getEventPlatform(p);
      const device = p.device || p.$device_name;
      return [platform, device].filter(Boolean).join(" · ") || "—";
    },
  },
];

const EventFilters = ({ filters, onChange, options, compact = false }) => (
  <div className={`analytics-event-filters${compact ? " analytics-event-filters--compact" : ""}`}>
    <Input.Search
      placeholder="Search events…"
      value={filters.search}
      onChange={(e) => onChange({ ...filters, search: e.target.value })}
      allowClear
      style={{ minWidth: compact ? 160 : 220 }}
    />
    <Select
      value={filters.event}
      onChange={(event) => onChange({ ...filters, event })}
      options={[
        { value: "all", label: "All events" },
        ...options.events.map((name) => ({ value: name, label: name })),
      ]}
      popupMatchSelectWidth={false}
      style={{ minWidth: compact ? 140 : 180 }}
    />
    <Select
      value={filters.platform}
      onChange={(platform) => onChange({ ...filters, platform })}
      options={[
        { value: "all", label: "All platforms" },
        ...options.platforms.map((name) => ({ value: name, label: name })),
      ]}
      popupMatchSelectWidth={false}
      style={{ minWidth: compact ? 120 : 150 }}
    />
    <Select
      value={filters.version}
      onChange={(version) => onChange({ ...filters, version })}
      options={[
        { value: "all", label: "All versions" },
        ...options.versions.map((name) => ({ value: name, label: name })),
      ]}
      popupMatchSelectWidth={false}
      style={{ minWidth: compact ? 110 : 140 }}
    />
    {(filters.search ||
      filters.event !== "all" ||
      filters.platform !== "all" ||
      filters.version !== "all") && (
      <Button
        type="link"
        size="small"
        onClick={() =>
          onChange({ search: "", event: "all", platform: "all", version: "all" })
        }
      >
        Clear filters
      </Button>
    )}
  </div>
);

const DEFAULT_FILTERS = { search: "", event: "all", platform: "all", version: "all" };

const AnalyticsUserEventsModal = ({
  open,
  onClose,
  events = [],
  userName,
  userId,
  initialFilters = DEFAULT_FILTERS,
}) => {
  const [filters, setFilters] = useState(initialFilters);

  useEffect(() => {
    if (open) setFilters(initialFilters);
  }, [open, initialFilters]);

  const filterOptions = useMemo(() => buildFilterOptions(events), [events]);
  const filtered = useMemo(() => filterEvents(events, filters), [events, filters]);

  const handleExport = () => {
    exportEventsCsv(filtered, `user-${userId || "events"}-export.csv`);
  };

  return (
    <Modal
      open={open}
      title={userName ? `All events — ${userName}` : "All events"}
      onCancel={onClose}
      footer={null}
      width={1180}
      destroyOnClose
      className="analytics-detail-modal analytics-user-events-modal"
      {...analyticsModalOverlayProps}
      classNames={{ body: "analytics-detail-modal__body" }}
    >
      <div className="analytics-user-events-modal__toolbar">
        <p className="analytics-detail-desc">
          {filtered.length} of {events.length} event{events.length === 1 ? "" : "s"} shown.
          Filter by event, platform, version, or search text.
        </p>
        <Button size="small" onClick={handleExport} disabled={!filtered.length}>
          <i className="fa-solid fa-download" /> Export CSV
        </Button>
      </div>
      <EventFilters filters={filters} onChange={setFilters} options={filterOptions} />
      <Table
        size="small"
        className="analytics-table"
        columns={eventTableColumns}
        dataSource={filtered.map((r, i) => ({ ...r, key: `${r.timestamp}-${i}` }))}
        pagination={{
          pageSize: 25,
          size: "small",
          showSizeChanger: true,
          pageSizeOptions: ["25", "50", "100"],
        }}
        scroll={{ x: 980 }}
        locale={{ emptyText: "No events match your filters" }}
      />
    </Modal>
  );
};

export {
  DEFAULT_FILTERS,
  EventFilters,
  eventTableColumns,
  filterEvents,
  buildFilterOptions,
  formatEventDetails,
};
export default AnalyticsUserEventsModal;
